import React, { useEffect, useRef } from "react";
import { Form } from "antd";
import "intl-tel-input/build/css/intlTelInput.css";

const IntlPhoneInput = ({
  form,
  name,
  label,
  required = true,
  placeholder = "Enter phone number",
  disabled = false,
  value: propValue,
  onChange: propOnChange,
  defaultCountry = "auto",
}) => {
  const inputRef = useRef(null);
  const itiRef = useRef(null);

  // Initialize intl-tel-input only on client
  useEffect(() => {
    if (!inputRef.current) return;

    let mounted = true;
    const initPhoneInput = () => {
      if (!mounted) return;

      itiRef.current = window.intlTelInput(inputRef.current, {
        initialCountry: defaultCountry || "auto",
        preferredCountries: ["in", "ae", "gb"],
        geoIpLookup: (callback) => {
          if (defaultCountry === "auto") {
            fetch("https://ipapi.co/json")
              .then((res) => res.json())
              .then((data) => callback(data.country_code || "in"))
              .catch(() => callback("in"));
          } else {
            callback(defaultCountry);
          }
        },
        loadUtils: () => import("intl-tel-input/utils"),
        formatOnDisplay: true,
        useFullscreenPopup: false
      });

      // Handle value changes
      const handleChange = () => {
        if (itiRef.current && inputRef.current) {
          try {
            const number = itiRef.current.getNumber();
            // Only update if we have a valid number
            if (number) {
              // Update the form field value
              if (form && name) {
                form.setFieldsValue({ [name]: number });
              }
              // Call prop onChange if provided
              if (propOnChange) {
                propOnChange(number);
              }
            }
          } catch (error) {
            console.warn('Error handling phone number change:', error.message);
          }
        }
      };

      // Initialize with value if provided
      try {
        if (propValue) {
          itiRef.current.setNumber(propValue);
        } else if (form && name) {
          // Get initial value from form
          const formValue = form.getFieldValue(name);
          if (formValue) {
            itiRef.current.setNumber(formValue);
          }
        }
      } catch (error) {
        console.warn('Error initializing phone number:', error.message);
      }

      // Add event listeners
      inputRef.current.addEventListener('input', handleChange);
      inputRef.current.addEventListener('countrychange', handleChange);

      // Cleanup function
      return () => {
        if (inputRef.current) {
          inputRef.current.removeEventListener('input', handleChange);
          inputRef.current.removeEventListener('countrychange', handleChange);
        }
      };
    };

    // Load the intl-tel-input library if not already loaded
    if (window.intlTelInput) {
      initPhoneInput();
    } else {
      import("intl-tel-input").then(({ default: intlTelInput }) => {
        window.intlTelInput = intlTelInput;
        initPhoneInput();
      });
    }

    return () => {
      mounted = false;
      if (itiRef.current) {
        itiRef.current.destroy();
        itiRef.current = null;
      }
    };
  }, [defaultCountry]);

  // Reset phone input when defaultCountry changes
  useEffect(() => {
    if (itiRef.current) {
      try {
        // Only set country if we have a valid country code
        if (defaultCountry && defaultCountry !== 'auto') {
          itiRef.current.setCountry(defaultCountry);
          // Trigger change after country change to update the number format
          if (inputRef.current) {
            const event = new Event('input', { bubbles: true });
            inputRef.current.dispatchEvent(event);
          }
        }
      } catch (error) {
        console.warn('Error setting country:', error.message);
      }
    }
  }, [defaultCountry]);


  return (
    <Form.Item
      name={name}
      className="country-dropdown"
      label={label}
      rules={[
        { required, message: "Please enter phone number!" },
        {
          validator(_, val) {
            if (!val) return Promise.resolve();

            if (itiRef.current?.isValidNumber()) return Promise.resolve();
            return Promise.reject(new Error("Invalid Contact Number"));
          },
        },
      ]}
      style={{ marginBottom: 0 , width:'100%' }}
    >
      <input
        ref={inputRef}
        type="tel"
        key={`phone-${defaultCountry}`}
        disabled={disabled}
        placeholder={placeholder}
        style={{
          border: '1px solid #d1d5db',
          background : '#f7f9fd',
          borderRadius: '0.375rem',
          padding: '0.5rem 0.75rem',
          width: '100%',
          outline: 'none',
          boxSizing: 'border-box',
          fontSize: '1rem',
          lineHeight: '1.5',
          transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
        }}
        onFocus={(e) => {
          e.target.style.borderColor = '#3b82f6';
          e.target.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.5)';
        }}
        onBlur={(e) => {
          e.target.style.borderColor = '#d1d5db';
          e.target.style.boxShadow = 'none';
        }}
      />
    </Form.Item>
  );
};

export default IntlPhoneInput;
